import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { stripProductenSectionFromContent } from '../utils/stripProductenSection';
import { getQuotationLabels, formatQuotationDate, normalizeQuotationLanguage } from '../utils/quotationLanguages';
import './QuotationPreview.css';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function toValidProductId(id) {
  if (id == null || id === '') return null;
  const s = String(id).trim();
  return UUID_REGEX.test(s) ? s : null;
}

const QuotationPreview = ({ quotationData, products, fieldLabels, fieldLabelsEn, subscriberId: subscriberIdProp, selectedCustomer, existingQuotationId, onBack, onSaved, onSaveAndContinue, onSkipToNext }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [offertebijlageError, setOffertebijlageError] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [brandColor, setBrandColor] = useState(null);
  const [companyPayoff, setCompanyPayoff] = useState('');
  const [companyNameFromSub, setCompanyNameFromSub] = useState('');
  const [companyFooterDetails, setCompanyFooterDetails] = useState(null);
  const subscriberId = subscriberIdProp || (typeof localStorage !== 'undefined' && localStorage.getItem('selectedSubscriberId'));

  useEffect(() => {
    if (!subscriberId) {
      setLogoUrl(null);
      setBrandColor(null);
      setCompanyPayoff('');
      setCompanyNameFromSub('');
      setCompanyFooterDetails(null);
      return;
    }
    let cancelled = false;
    api.get(`/api/subscribers/${subscriberId}`)
      .then((res) => {
        if (cancelled) return;
        const s = res.data || {};
        const cd = s.company_details || {};
        if (s.logo_url) setLogoUrl(s.logo_url);
        if (cd.brand_color) setBrandColor(cd.brand_color);
        setCompanyPayoff(cd.payoff || '');
        setCompanyNameFromSub(s.company_name || '');
        if (cd.address || cd.phone || cd.email || cd.kvk || cd.bank) {
          setCompanyFooterDetails({
            companyName: s.company_name || '',
            address: typeof cd.address === 'string' ? cd.address : (cd.address ? String(cd.address) : ''),
            phone: typeof cd.phone === 'string' ? cd.phone : (cd.phone ? String(cd.phone) : ''),
            email: typeof cd.email === 'string' ? cd.email : (cd.email ? String(cd.email) : ''),
            kvk: typeof cd.kvk === 'string' ? cd.kvk : (cd.kvk ? String(cd.kvk) : ''),
            bank: typeof cd.bank === 'string' ? cd.bank : (cd.bank ? String(cd.bank) : '')
          });
        } else {
          setCompanyFooterDetails(null);
        }
      })
      .catch(() => { if (!cancelled) setLogoUrl(null); });
    return () => { cancelled = true; };
  }, [subscriberId]);

  const L = getQuotationLabels(
    quotationData.language || 'nl',
    fieldLabels || {},
    fieldLabelsEn || {}
  );

  const handleSave = async (onSuccessCallback) => {
    const sid = subscriberIdProp || (typeof localStorage !== 'undefined' && localStorage.getItem('selectedSubscriberId')) || subscriberId;
    if (!sid || sid === '') {
      setError('Selecteer eerst een bedrijf in de header (dropdown Bedrijf).');
      return null;
    }
    setLoading(true);
    setError(null);
    setOffertebijlageError(null);
    setSavedId(null);
    try {
      const allNamedProducts = (products || []).filter(p => p.name && !isOffertebijlage(p.name));
      const productIds = allNamedProducts.map(p => toValidProductId(p.id));
      const productsPayload = allNamedProducts.map(p => ({
        name: p.name || '',
        quantity: p.quantity ?? 1,
        price: parseFloat(p.price) || 0,
        description: p.description || ''
      }));
      const payload = {
        subscriberId: sid,
        quotationData: {
          title: quotationData.title,
          client_name: quotationData.clientName,
          client_data: quotationData.clientData,
          client_odoo_partner_id: selectedCustomer?.odoo_partner_id ?? undefined,
          description: quotationData.description,
          highlight: quotationData.highlight,
          closing: quotationData.closing,
          greeting: quotationData.greeting,
          reference: quotationData.reference,
          badge: quotationData.badge,
          company_contact: quotationData.companyContact,
          valid_until: quotationData.validUntil,
          total_price: quotationData.pricing?.total ?? 0,
          payment_terms: quotationData.pricing?.paymentTerms,
          payment_description: quotationData.pricing?.paymentDescription,
          price_subtitle: quotationData.pricing?.subtitle,
          language: quotationData.language || 'nl',
          show_one_time: quotationData.pricing?.showOneTime,
          one_time_amount: quotationData.pricing?.oneTimeAmount,
          one_time_amount_manual: quotationData.pricing?.oneTimeAmountManual ?? false,
          show_monthly: quotationData.pricing?.showMonthly,
          monthly_amount: quotationData.pricing?.monthlyAmount,
          standaardinhoud: quotationData.standaardinhoud || '',
          standaardinhoud_template: quotationData.standaardinhoudTemplate || '',
          offer_toelichting: quotationData.offerToelichting || '',
          ai_query: quotationData.aiQuery || '',
          products: productsPayload
        },
        productIds: productIds.length ? productIds : []
      };
      let response;
      let resultId;
      if (existingQuotationId) {
        // Update bestaande offerte
        response = await api.put(`/api/quotations/${existingQuotationId}`, {
          ...payload.quotationData,
          products: productsPayload,
          productIds: payload.productIds
        }, {
          headers: { 'X-Subscriber-Id': sid }
        });
        resultId = existingQuotationId;
      } else {
        // Maak nieuwe offerte aan
        response = await api.post('/api/quotations', payload, {
          headers: { 'X-Subscriber-Id': sid }
        });
        resultId = response.data.id;
        if (response.data.odoo_offertebijlage_error) {
          setOffertebijlageError(response.data.odoo_offertebijlage_error);
        }
      }
      setSavedId(resultId);
      setError(null); // bij succes altijd foutmelding wissen (voorkomt rode + groene melding tegelijk bij dubbele request)
      if (onSaved) onSaved(resultId);
      if (onSuccessCallback) onSuccessCallback(resultId);
      return resultId;
    } catch (err) {
      const serverError = err.response?.data?.error || '';
      const serverMessage = err.response?.data?.message || '';
      if (typeof serverError === 'string' && serverError.toLowerCase().includes('subscriber') && serverError.toLowerCase().includes('user')) {
        setError('Selecteer een bedrijf in de header (dropdown "Bedrijf:") voordat je opslaat.');
      } else {
        const displayError = serverError || 'Opslaan mislukt';
        setError(serverMessage && serverMessage !== displayError ? `${displayError}: ${serverMessage}` : displayError);
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (savedId) onSaveAndContinue(savedId);
  };

  const formatEuro = (amount) => `€ ${(parseFloat(amount) || 0).toFixed(2).replace('.', ',')}`;
  const pricing = quotationData.pricing || {};
  const quotationLanguage = normalizeQuotationLanguage(quotationData.language);
  const pricingDefaults = quotationLanguage === 'en'
    ? { subtitle: 'excl. VAT', paymentDescription: '40% deposit upon agreement • 60% upon delivery' }
    : { subtitle: 'excl. btw', paymentDescription: '40% aanbetaling • 60% bij oplevering' };
  const showOneTime = pricing.showOneTime !== false;
  const showMonthly = !!pricing.showMonthly;
  const oneTimeAmount = pricing.oneTimeAmount ?? pricing.total ?? 0;
  const monthlyAmount = pricing.monthlyAmount ?? 0;
  const isOffertebijlage = (name) => /^Offertebijlage\b/i.test(name);
  const productRows = (products || []).filter(p => p.name && !isOffertebijlage(p.name));
  const descriptionForPreview = quotationData.description
    ? stripProductenSectionFromContent(quotationData.description)
    : '';
  const standaardinhoudForPreview = quotationData.standaardinhoud
    ? stripProductenSectionFromContent(quotationData.standaardinhoud)
    : '';

  return (
    <div className="quotation-preview">
      <div className="preview-actions">
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          ← Terug naar aanpassen
        </button>
        {!savedId ? (
          <>
            <button
              type="button"
              className="btn btn-primary"
              data-demo-step="demo-step-5"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Opslaan...' : existingQuotationId ? '✓ Offerte bijwerken' : '✓ Opslaan'}
            </button>
            {onSkipToNext && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={async () => {
                  if (savedId) {
                    onSaveAndContinue(savedId);
                  } else {
                    await handleSave((resultId) => {
                      if (onSaveAndContinue) onSaveAndContinue(resultId);
                    });
                  }
                }}
                disabled={loading}
              >
                {loading ? 'Opslaan...' : 'Doorgaan naar volgende stap'}
              </button>
            )}
          </>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleContinue}
          >
            Doorgaan naar PDF-stap
          </button>
        )}
      </div>
      {error && !savedId && <div className="error">{error}</div>}
      {productRows.length === 0 && existingQuotationId && (
        <div className="preview-no-products-hint" style={{ marginBottom: 8, padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, color: '#0369a1', fontSize: 14 }}>
          Deze offerte heeft geen productregels. Ga naar &quot;Aanpassen&quot; om producten toe te voegen.
        </div>
      )}
      {savedId && (
        <div className="success" style={{ marginBottom: 8 }}>Offerte opgeslagen.</div>
      )}
      {offertebijlageError && (
        <div className="error" style={{ marginBottom: 8 }}>
          Offertebijlage-product niet automatisch aangemaakt: {offertebijlageError}
          <br />
          <small>Op de volgende stap kun je op &quot;Offertebijlage-product aanmaken in Odoo&quot; klikken om het alsnog te proberen.</small>
        </div>
      )}

      <div className="preview-paper" style={brandColor ? { '--preview-brand': brandColor } : undefined}>
        <header className="preview-header">
          <div className="preview-brand">
            {logoUrl && (
              <div className="preview-logo-wrap">
                <img src={logoUrl} alt="" className="preview-logo" />
              </div>
            )}
            {!logoUrl && (
              <h1 className="preview-company" style={brandColor ? { color: brandColor } : undefined}>
                {quotationData.companyName || companyNameFromSub || ''}
              </h1>
            )}
            {(quotationData.companyTagline || companyPayoff) && (
              <p className="preview-tagline">{quotationData.companyTagline || companyPayoff}</p>
            )}
            {quotationData.badge && <span className="preview-badge">{quotationData.badge}</span>}
          </div>
          <div className="preview-meta">
            <h2 className="preview-title">{quotationData.title || 'Offertevoorstel'}</h2>
            <p><strong>{L.date}:</strong> {quotationData.date || formatQuotationDate(quotationLanguage)}</p>
            {quotationData.validUntil && (
              <p><strong>{L.validUntil}:</strong> {quotationData.validUntil}</p>
            )}
            <p><strong>{L.client}:</strong> {quotationData.clientName || '—'}</p>
            <p><strong>{L.reference}:</strong> {quotationData.reference || '—'}</p>
          </div>
        </header>

        <hr className="preview-divider" />

        {quotationData.greeting && (
          <section className="preview-section preview-section-card">
            <div className="preview-text white-space">{quotationData.greeting}</div>
          </section>
        )}

        {descriptionForPreview && (
          <section className="preview-section preview-section-card">
            <div className="preview-text white-space">{descriptionForPreview}</div>
          </section>
        )}

        {(quotationData.highlight != null && quotationData.highlight !== '') && (
          <section className="preview-section preview-highlight">
            <h4>{L.highlightBoxTitle}</h4>
            <div className="preview-text white-space">{quotationData.highlight}</div>
          </section>
        )}

        {quotationData.clientData && (
          <section className="preview-section">
            <h4>{L.clientDataSectionTitle}</h4>
            <div className="preview-text white-space">{quotationData.clientData}</div>
          </section>
        )}

        <section className="preview-section">
          <h3>{L.investment}</h3>
          <div className="preview-pricing-row">
            {showOneTime && (
              <div className="preview-price-box">
                <span className="preview-price-label">{L.oneTime}</span>
                <span className="preview-price-value">{formatEuro(oneTimeAmount)}</span>
                <span className="preview-price-sub">{pricing.subtitle || pricingDefaults.subtitle}</span>
              </div>
            )}
            {showMonthly && (
              <div className="preview-price-box">
                <span className="preview-price-label">{L.monthly}</span>
                <span className="preview-price-value">{formatEuro(monthlyAmount)}</span>
                <span className="preview-price-sub">{pricing.subtitle || pricingDefaults.subtitle}</span>
              </div>
            )}
            <div className="preview-price-box">
              <span className="preview-price-label">{L.payment}</span>
              <span className="preview-price-value">{pricing.paymentTerms || '40% / 60%'}</span>
              <span className="preview-price-sub">{pricing.paymentDescription || pricingDefaults.paymentDescription}</span>
            </div>
          </div>
        </section>

        {standaardinhoudForPreview && (
          <>
            <hr className="preview-divider" />
            <section className="preview-section">
              {quotationData.standaardinhoudTemplate && (
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>Template: {quotationData.standaardinhoudTemplate}</div>
              )}
              <div dangerouslySetInnerHTML={{ __html: standaardinhoudForPreview }} />
            </section>
          </>
        )}

        {quotationData.offerToelichting && (
          <>
            <hr className="preview-divider" />
            <section className="preview-section">
              <div dangerouslySetInnerHTML={{ __html: quotationData.offerToelichting }} />
            </section>
          </>
        )}

        {(quotationData.closing != null && quotationData.closing !== '') && (
          <section className="preview-section">
            <h3>{L.closingGreeting}</h3>
            <div className="preview-text white-space">{quotationData.closing}</div>
          </section>
        )}

        {quotationData.companyContact && (
          <section className="preview-section preview-signature">
            <div className="preview-text white-space preview-company-contact">{quotationData.companyContact}</div>
          </section>
        )}

        {quotationData.notes && (
          <section className="preview-section">
            <div className="preview-text white-space">{quotationData.notes}</div>
          </section>
        )}

        {companyFooterDetails && (
          <footer className="preview-paper-footer">
            {[
              companyFooterDetails.companyName,
              typeof companyFooterDetails.address === 'string' ? companyFooterDetails.address.replace(/\n/g, ', ') : companyFooterDetails.address,
              companyFooterDetails.phone && `Tel: ${companyFooterDetails.phone}`,
              companyFooterDetails.email,
              companyFooterDetails.kvk && `KvK: ${companyFooterDetails.kvk}`,
              companyFooterDetails.bank && `IBAN: ${companyFooterDetails.bank}`
            ].filter(Boolean).join(' • ')}
          </footer>
        )}
      </div>
    </div>
  );
};

export default QuotationPreview;
